import React, { useId, useRef, useEffect } from "react";
import { Check, Minus } from "lucide-react";
import { cn } from "../../lib/cn";

/**
 * Checkbox
 * @param {string} [label]
 * @param {boolean} [checked]
 * @param {(event: React.ChangeEvent<HTMLInputElement>) => void} [onChange]
 * @param {boolean} [disabled]
 * @param {boolean} [indeterminate]
 * @param {string} [className]
 * @param {string} [id]
 */
export const Checkbox = React.forwardRef(function Checkbox(
  { label, checked = false, onChange, disabled = false, indeterminate = false, className, id, ...props },
  forwardedRef
) {
  const generatedId = useId();
  const checkboxId = id || generatedId;
  const innerRef = useRef(null);

  useEffect(() => {
    if (innerRef.current) {
      innerRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <label
      htmlFor={checkboxId}
      className={cn(
        "inline-flex items-center gap-2 text-sm text-neutral-900 dark:text-neutral-100",
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
        className
      )}
    >
      <span className="relative flex h-5 w-5 shrink-0 items-center justify-center">
        <input
          ref={(node) => {
            innerRef.current = node;
            if (typeof forwardedRef === "function") forwardedRef(node);
            else if (forwardedRef) forwardedRef.current = node;
          }}
          type="checkbox"
          id={checkboxId}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          aria-checked={indeterminate ? "mixed" : checked}
          className={cn(
            "peer absolute h-5 w-5 cursor-pointer appearance-none rounded border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 transition-colors",
            "checked:border-primary-900 checked:bg-primary-900",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900",
            "disabled:cursor-not-allowed"
          )}
          {...props}
        />
        {(checked || indeterminate) && (
          <span className="pointer-events-none relative text-white">
            {indeterminate ? (
              <Minus className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
            )}
          </span>
        )}
      </span>
      {label && <span>{label}</span>}
    </label>
  );
});
